import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  CheckSquare,
  Clock,
  AlertCircle,
  TrendingUp,
  Calendar,
  User,
  FolderOpen
} from 'lucide-react'
import DataTable from '../../../components/DataTable/DataTable'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import taskService from '../../../services/taskService'
import RevisionHistoryModal from '../../../components/RevisionHistoryModal/RevisionHistoryModal'
import '../../../components/RevisionHistoryModal/RevisionHistoryModal.css'
import '../../../components/ConfirmationModal/ConfirmationModal.css'

const TaskList = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalTasks: 0,
    notStartedTasks: 0,
    inProgressTasks: 0,
    onHoldTasks: 0,
    completedTasks: 0,
    cancelledTasks: 0,
    overdueTasks: 0,
    completionRate: 0
  })
  const [showRevisionHistory, setShowRevisionHistory] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [selectedTaskData, setSelectedTaskData] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [tasksResponse, statsResponse] = await Promise.all([
        taskService.getAllTasks(),
        taskService.getTaskStats()
      ])

      if (tasksResponse.success !== false && tasksResponse.tasks) {
        setData(tasksResponse.tasks || [])
        
        // Show toast if no data
        if ((tasksResponse.tasks || []).length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No tasks found. Create your first task to get started!')
        }
      } else {
        toast.error(tasksResponse.message || 'Failed to load tasks')
      }

      if (statsResponse.success) {
        setStats(statsResponse.stats || {})
      }
    } catch (error) {
      console.error('Error loading data:', error)
      if (!error._errorHandled) {
        toast.error('Failed to load tasks data')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleAddNew = () => {
    navigate('/projects/tasks/new')
  }

  const handleEdit = (task) => {
    const taskId = task._id || task.id
    if (!taskId) {
      toast.error('Task ID not found')
      return
    }
    navigate(`/projects/tasks/edit/${taskId}`)
  }

  const handleView = (task) => {
    const taskId = task._id || task.id
    if (!taskId) {
      toast.error('Task ID not found')
      return
    }
    navigate(`/projects/tasks/view/${taskId}`)
  }

  const handleDelete = (task) => {
    setDeleteItem(task)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const taskId = deleteItem._id || deleteItem.id
      if (!taskId) {
        toast.error('Task ID not found')
        return
      }
      const response = await taskService.deleteTask(taskId)
      if (response.success) {
        toast.success('Task deleted successfully')
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete task')
      }
    } catch (error) {
      console.error('Error deleting task:', error)
      if (!error._errorHandled) {
        toast.error('Failed to delete task')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const handleRevisionHistory = (task) => {
    if (!task) {
      toast.error('Task data not found')
      return
    }
    setSelectedTaskData(task)
    setShowRevisionHistory(true)
  }

  const handleRowSelection = (selectedRows) => {
    setSelectedRows(selectedRows)
  }

  const handleSelectionChange = (selectedRows) => {
    setSelectedRows(selectedRows)
  }

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const day = String(date.getDate()).padStart(2, '0')
    const month = months[date.getMonth()]
    const year = date.getFullYear()
    return `${day}-${month}-${year}`
  }

  // Define columns for the data table
  const columns = [
    {
      key: 'taskNumber',
      label: 'Task #',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">
          {value}
        </div>
      )
    },
    {
      key: 'taskName',
      label: 'Task Name',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">
          {value}
        </div>
      )
    },
    {
      key: 'project',
      label: 'Project',
      sortable: true,
      render: (value, row) => {
        const project = row.project
        if (!project) return <span className="text-gray-500">N/A</span>
        const projectName = typeof project === 'string' 
          ? project 
          : (project.projectName || project.name || 'N/A')
        return (
          <div className="font-medium text-gray-900">
            {projectName}
          </div>
        )
      }
    },
    {
      key: 'assignedTo',
      label: 'Assigned To',
      sortable: true,
      render: (value, row) => {
        const assignee = row.assignedTo
        if (!assignee) return <span className="text-gray-500">N/A</span>
        const assigneeName = typeof assignee === 'string' 
          ? assignee 
          : (assignee.employeeName || assignee.name || 'N/A')
        return (
          <div className="font-medium text-gray-900">
            {assigneeName}
          </div>
        )
      }
    },
    {
      key: 'taskStatus',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusColors = {
          'Not Started': '#6B7280',
          'In Progress': '#3B82F6',
          'On Hold': '#F59E0B',
          'Completed': '#10B981',
          'Cancelled': '#EF4444'
        }
        const statusColor = statusColors[value] || '#6B7280'
        return (
          <span 
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ 
              backgroundColor: `${statusColor}20`,
              color: statusColor,
              border: `1px solid ${statusColor}40`
            }}
          >
            {value || 'N/A'}
          </span>
        )
      }
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      render: (value, row) => {
        const priority = row.priority
        if (!priority) return <span className="text-gray-500">N/A</span>
        const priorityName = typeof priority === 'string' 
          ? priority 
          : (priority.priorityName || priority.name || 'N/A')
        const priorityColor = typeof priority === 'object' 
          ? (priority.priorityColor || '#6B7280')
          : '#6B7280'
        return (
          <span 
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ 
              backgroundColor: `${priorityColor}20`,
              color: priorityColor,
              border: `1px solid ${priorityColor}40`
            }}
          >
            {priorityName}
          </span>
        )
      }
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">
          {formatDate(value)}
        </div>
      )
    },
    {
      key: 'progress',
      label: 'Progress',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <div className="w-16 bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full" 
              style={{ width: `${value || 0}%` }}
            ></div>
          </div>
          <span className="text-sm text-gray-600">{value || 0}%</span>
        </div>
      )
    }
  ]

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Transactions', path: '/projects?tab=transactions', onClick: () => navigate('/projects?tab=transactions') },
    { label: 'Task List', path: '/projects/tasks/list', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Task List</h1>
          <p>Create, view, and manage all tasks</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAddNew}
          >
            <Plus size={16} />
            Add New Task
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckSquare size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalTasks || 0}</div>
          <div className="metric-title">Total Tasks</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingUp size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.completedTasks || 0}</div>
          <div className="metric-title">Completed</div>
          <div className="metric-subtitle">This month</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+5%</span>
            </div>
          </div>
          <div className="metric-value">{stats.inProgressTasks || 0}</div>
          <div className="metric-title">In Progress</div>
          <div className="metric-subtitle">Currently active</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <AlertCircle size={16} />
            </div>
            <div className="metric-trend negative">
              <TrendingUp size={12} />
              <span>+3%</span>
            </div>
          </div>
          <div className="metric-value">{stats.overdueTasks || 0}</div>
          <div className="metric-title">Overdue</div>
          <div className="metric-subtitle">Need attention</div>
        </div>
      </div>

      {/* Task List Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onRevisionHistory={handleRevisionHistory}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search tasks..."
          emptyMessage="No tasks found. Create your first task to get started!"
          noCard={true}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Task"
        message={`Are you sure you want to delete "${deleteItem?.taskName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Revision History Modal */}
      <RevisionHistoryModal
        isOpen={showRevisionHistory}
        onClose={() => setShowRevisionHistory(false)}
        data={selectedTaskData}
        title="Task Revision History"
      />
    </div>
  )
}

export default TaskList

